import { Component, Input, OnDestroy } from '@angular/core';
import { NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { TranslocoService } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import Swal from 'sweetalert2';
import { ModalService } from 'projects/shared/modal/modal.service';
import { EnumsService } from '../../../services/enums.service';
import { WizardFlowServiceBase } from '../../../services/wizard/wizard-flow-service.base';
import { Liability, RealEstateOwned } from '../../../models/mortgage.model';
import { MortgageApplication } from '../../../models/mortgage-app.model';
import { LienDialogComponent, LienOwnerOption } from '../lien-dialog/lien-dialog.component';
import { syncReoMortgageLiability } from '../reo-liability-sync';
import {
  addOrUpdateLien, cloneLienForEdit, createLien, defaultLienOwnerIds, getReoLiens,
  maskAccountNumber, recalculateAmountOfMortgage, removeLien,
} from '../reo-liens';

/**
 * LLS-569 item 3: the "existing liens" section an REO form shows instead of a single balance
 * input. Lists the REO's liens, opens the lien dialog to add / edit, deletes after a confirm, and
 * after every change re-derives reo.amountOfMortgage and lets reo-liability-sync drop its
 * auto-created placeholder. Hosts pass the `reo.amountOfMortgage` field-config label and
 * required flag and call validate() from their own validation.
 */
@Component({
    selector: 'reo-liens',
    templateUrl: 'reo-liens.component.html',
    standalone: false
})
export class ReoLiensComponent implements OnDestroy {

  @Input()
  mortgage!: MortgageApplication;

  @Input()
  reo!: RealEstateOwned;

  @Input()
  label: string = '';

  @Input()
  required: boolean = false;

  showRequiredError: boolean = false;

  private readonly _modalOptions: NgbModalOptions = {
    size: 'lg',
    backdrop: 'static',
    container: 'body',
  };

  private _dialogSubscription: Subscription | undefined;

  constructor(private readonly _modalService: ModalService,
    private readonly _wizardFlowService: WizardFlowServiceBase,
    private readonly _enumsService: EnumsService,
    private readonly _transloco: TranslocoService) {
  }

  get liens(): Liability[] {
    return getReoLiens(this.mortgage, this.reo);
  }

  /** A borrower who answered the old single-balance question has already satisfied Required;
   *  the first lien they add replaces that figure. */
  private get requirementMet(): boolean {
    return this.liens.length > 0 || !!this.reo?.amountOfMortgage;
  }

  ngOnDestroy(): void {
    this._dialogSubscription?.unsubscribe();
  }

  /** Called by the host's own validation; only a required, empty section (with no on-file balance) fails. */
  validate = (): boolean => {
    this.showRequiredError = this.required && !this.requirementMet;
    return !this.showRequiredError;
  }

  ownerNames = (lien: Liability): string => {
    const borrowers = this.mortgage?.borrowers ?? [];
    return (lien.owningBorrowerIds ?? [])
      .map(id => borrowers.find(b => b.borrowerId === id))
      .filter(b => !!b)
      .map(b => `${b!.firstName} ${b!.lastName}`)
      .join(', ');
  }

  positionName = (lien: Liability): string => {
    const match = this._enumsService.lienTypes.find(t => t.value === lien.lienPosition);
    return match ? match.name : (lien.lienPosition ?? '');
  }

  maskedAccount = (lien: Liability): string => maskAccountNumber(lien.accountNumber);

  onAddClicked = (): void => {
    const lien = createLien(this.reo, defaultLienOwnerIds(this.mortgage, this.reo));
    this.openDialog(lien, false);
  }

  onEditClicked = (lien: Liability): void => {
    // LLS-569: a credit-pull liability represents real data we must never overwrite, move, or delete.
    if (lien.fromCreditReport) { return; }
    this.openDialog(cloneLienForEdit(lien), true);
  }

  onDeleteClicked = async (lien: Liability): Promise<void> => {
    // LLS-569: a credit-pull liability represents real data we must never overwrite, move, or delete.
    if (lien.fromCreditReport) { return; }
    const result = await Swal.fire({
      title: this._transloco.translate('common.areYouSure'),
      text: this._transloco.translate('wizard.reo.deleteLienConfirm'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: this._transloco.translate('common.yesDeleteIt')
    });
    if (!result?.isConfirmed) {
      return;
    }
    removeLien(this.mortgage, this.reo, lien);
    this.afterChange();
  }

  private openDialog(lien: Liability, inEditMode: boolean): void {
    const inCoApplyFlow = this._wizardFlowService.inCoApplyFlow;
    if (inCoApplyFlow) {
      lien.owningBorrowerIds = [<number>this._wizardFlowService.context.currentBorrower.borrowerId];
    }
    const modal = this._modalService.show(LienDialogComponent, this._modalOptions);
    modal.componentInstance.lien = lien;
    modal.componentInstance.inEditMode = inEditMode;
    modal.componentInstance.inCoApplyFlow = inCoApplyFlow;
    modal.componentInstance.possibleOwners = this.possibleOwners();
    this._dialogSubscription?.unsubscribe();
    this._dialogSubscription = modal.componentInstance.saveClicked.subscribe((saved: Liability) => {
      addOrUpdateLien(this.mortgage, this.reo, saved);
      this.afterChange();
    });
    // A dismissed ng-bootstrap modal rejects its result promise; swallow it like liability-reo does.
    modal.result.then(() => { }, () => { });
  }

  private possibleOwners(): LienOwnerOption[] {
    return (this._wizardFlowService.context.mortgage.borrowers ?? [])
      .filter(b => b.borrowerId != null)
      .map(b => ({ id: <number>b.borrowerId, name: `${b.firstName} ${b.lastName}` }));
  }

  private afterChange(): void {
    // Recalculate first: getReoLiens ignores the sync's own placeholder, so this total is always
    // derived from real liens only. Sync then reconciles its placeholder against that total - add:
    // real liens exist, so the placeholder is dropped; delete-to-zero: amount is undefined, so the
    // placeholder is dropped and none is re-created.
    recalculateAmountOfMortgage(this.mortgage, this.reo);
    syncReoMortgageLiability(this.mortgage, this.reo);
    this.showRequiredError = this.required && !this.requirementMet;
  }
}
